import { safeParseDateInput } from '../../../helpers/date-time-helpers/parsing/parsing';
import { isFutureDate } from '../dashboards.helpers';
import type { SprintChartDataPoint, SprintData } from '../hooks/dashboards.hooks.type';

type BurnChartType = 'burn_up' | 'burn_down';
type BurnChartMetric = 'tasks' | 'points';

/**
 * A single rendered point for the burn chart. Spreads the source data point and
 * adds the resolved series values the Recharts `<Line>` components read.
 */
interface BurnChartDatum extends SprintChartDataPoint {
  totalScope?: number;
  actualCompleted?: number;
  idealCompleted?: number;
  actualRemaining?: number;
  idealRemaining?: number;
}

/**
 * Reads the scope (total) for the selected metric, treating 0 as a valid value.
 *
 * @param {SprintChartDataPoint} dataPoint - A transformed burns / flow-by-pace point
 * @param {BurnChartMetric} selectedMetric - Whether the widget is showing tasks or points
 * @returns {number} The total scope on that date for the selected metric
 */
const getScope = (dataPoint: SprintChartDataPoint, selectedMetric: BurnChartMetric): number =>
  selectedMetric === 'tasks'
    ? (dataPoint.tasks_total ?? dataPoint.scope ?? 0)
    : (dataPoint.points_total ?? dataPoint.scope ?? 0);

/**
 * Reads the actual-remaining value for the selected metric, falling back to the
 * generic `remaining` field the transforms compute as scope − completed.
 *
 * @param {SprintChartDataPoint} dataPoint - A transformed burns / flow-by-pace point
 * @param {BurnChartMetric} selectedMetric - Whether the widget is showing tasks or points
 * @returns {number} The remaining work on that date for the selected metric
 */
const getRemaining = (dataPoint: SprintChartDataPoint, selectedMetric: BurnChartMetric): number =>
  selectedMetric === 'tasks'
    ? (dataPoint.tasks_remaining ?? dataPoint.remaining ?? 0)
    : (dataPoint.points_remaining ?? dataPoint.remaining ?? 0);

/**
 * Reads the backend-computed ideal (cumulative "ideal completed" pace) for the
 * selected metric. Returns `undefined` when the measure did not emit it so the
 * caller can fall back to a positional approximation.
 *
 * @param {SprintChartDataPoint} dataPoint - A transformed burns / flow-by-pace point
 * @param {BurnChartMetric} selectedMetric - Whether the widget is showing tasks or points
 * @returns {number | undefined} The ideal-completed value, or undefined when absent
 */
const getBackendIdeal = (dataPoint: SprintChartDataPoint, selectedMetric: BurnChartMetric): number | undefined =>
  selectedMetric === 'tasks' ? dataPoint.tasks_ideal : dataPoint.points_ideal;

/**
 * Builds the per-day series for a burn-up or burn-down dashboard widget.
 *
 * The "ideal" line is driven by the backend-computed ideal pace
 * (`tasks_ideal` / `points_ideal`), which ramps from 0 to the day's total scope
 * over the working days of the sprint. Burn-up renders that value directly as
 * "Ideal Pace"; burn-down derives "Ideal Remaining" as `scope − ideal` so it
 * descends to 0 at sprint end. When a measure does not provide the ideal (older
 * payloads), a positional linear approximation to the final scope is used.
 *
 * Responsibilities:
 * 1. Sort points chronologically BEFORE indexing so the positional fallback and
 *    the rendered order agree (the previous inline version indexed pre-sort).
 * 2. Resolve actual + total-scope series per the selected metric.
 * 3. Resolve the ideal series from the backend value, or fall back to a linear
 *    ramp toward the final (most recent) day's scope.
 *
 * @param {SprintData | undefined} data - The selected sprint's chart data
 * @param {BurnChartType} chartType - 'burn_up' or 'burn_down'
 * @param {BurnChartMetric} selectedMetric - 'tasks' or 'points'
 * @returns {BurnChartDatum[]} Chronologically sorted points ready for Recharts
 */
export const buildBurnChartData = (
  data: SprintData | undefined,
  chartType: BurnChartType,
  selectedMetric: BurnChartMetric,
): BurnChartDatum[] => {
  if (!data || !data.data || data.data.length === 0) return [];

  const points = data.data as SprintChartDataPoint[];

  const sorted = [...points].sort((a, b) => {
    const dateA = safeParseDateInput(a.date);
    const dateB = safeParseDateInput(b.date);
    // Push unparseable dates to the end deterministically rather than returning
    // 0, which would leave their position dependent on the input order.
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return dateA.valueOf() - dateB.valueOf();
  });

  // Fallback target = the scope on the final (most recent) day, i.e. the goal.
  const numSteps = Math.max(1, sorted.length - 1);
  const fallbackTargetScope = sorted.length > 0 ? getScope(sorted[sorted.length - 1], selectedMetric) : 0;
  const idealStep = fallbackTargetScope / numSteps;

  return sorted.map((dataPoint, index) => {
    // Not-yet-elapsed days come back from the backend as zeros for an
    // in-progress sprint. Keep the date on the x-axis but render no point
    // (undefined → Recharts gap) so the lines stop at today instead of
    // misleadingly dropping to 0.
    if (isFutureDate(dataPoint.date)) {
      return {
        ...dataPoint,
        date: dataPoint.date,
        totalScope: undefined,
        actualCompleted: undefined,
        idealCompleted: undefined,
        actualRemaining: undefined,
        idealRemaining: undefined,
      };
    }

    const scopeValue = getScope(dataPoint, selectedMetric);
    const remainingValue = getRemaining(dataPoint, selectedMetric);
    const backendIdeal = getBackendIdeal(dataPoint, selectedMetric);
    const hasBackendIdeal = typeof backendIdeal === 'number' && Number.isFinite(backendIdeal);

    if (chartType === 'burn_down') {
      const idealRemaining = hasBackendIdeal
        ? Math.max(0, scopeValue - (backendIdeal as number))
        : Math.max(0, fallbackTargetScope - index * idealStep);
      return {
        ...dataPoint,
        date: dataPoint.date,
        actualRemaining: Math.max(0, remainingValue),
        idealRemaining,
        totalScope: scopeValue,
      };
    }

    const actualCompleted = scopeValue - remainingValue;
    const idealCompleted = hasBackendIdeal
      ? Math.max(0, backendIdeal as number)
      : Math.min(fallbackTargetScope, index * idealStep);
    return {
      ...dataPoint,
      date: dataPoint.date,
      actualCompleted: Math.max(0, actualCompleted),
      idealCompleted,
      totalScope: scopeValue,
    };
  });
};

export type { BurnChartDatum, BurnChartMetric, BurnChartType };
